<script lang="ts">
  import { T, type Props } from '@threlte/core'
  import { useLoader } from '@threlte/core'
  import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
  import backLeftExtension from './models/back-left-extension.stl'
  import backLeftFoot from './models/back-left-foot.stl'
  import backLeftPart1 from './models/back-left-part-1.stl'
  import backLeftPart2 from './models/back-left-part-2.stl'
  import backLeftVerrin1 from './models/back-left-verrin-1.stl'
  import backLeftVerrin2 from './models/back-left-verrin-2.stl'
  import backRightExtension from './models/back-right-extension.stl'
  import backRightFoot from './models/back-right-foot.stl'
  import backRightPart1 from './models/back-right-part-1.stl'
  import backRightPart2 from './models/back-right-part-2.stl'
  import backRightVerrin1 from './models/back-right-verrin-1.stl'
  import backRightVerrin2 from './models/back-right-verrin-2.stl'
  import body from './models/body.stl'
  import frontLeftExtension from './models/front-left-extension.stl'
  import frontLeftFoot from './models/front-left-foot.stl'
  import frontLeftPart1 from './models/front-left-part-1.stl'
  import frontLeftPart2 from './models/front-left-part-2.stl'
  import frontLeftVerrin1 from './models/front-left-verrin-1.stl'
  import frontLeftVerrin2 from './models/front-left-verrin-2.stl'
  import frontRightExtension from './models/front-right-extension.stl'
  import frontRightFoot from './models/front-right-foot.stl'
  import frontRightPart1 from './models/front-right-part-1.stl'
  import frontRightPart2 from './models/front-right-part-2.stl'
  import frontRightVerrin1 from './models/front-right-verrin-1.stl'
  import frontRightVerrin2 from './models/front-right-verrin-2.stl'
  import sectionA from './models/section-a.stl'
  import sectionB from './models/section-b.stl'
  import sectionC from './models/section-c.stl'
  import sectionD from './models/section-d.stl'
  import sectionE from './models/section-e.stl'
  import sectionF from './models/section-f.stl'
  import verrin from './models/verrin.stl'
  import verticalAxis from './models/vertical-axis.stl'
  import type { Group } from 'three'

  const backLeftExtensionModel = useLoader(STLLoader).load(backLeftExtension)
  const backLeftFootModel = useLoader(STLLoader).load(backLeftFoot)
  const backLeftPart1Model = useLoader(STLLoader).load(backLeftPart1)
  const backLeftPart2Model = useLoader(STLLoader).load(backLeftPart2)
  const backLeftVerrin1Model = useLoader(STLLoader).load(backLeftVerrin1)
  const backLeftVerrin2Model = useLoader(STLLoader).load(backLeftVerrin2)
  const backRightExtensionModel = useLoader(STLLoader).load(backRightExtension)
  const backRightFootModel = useLoader(STLLoader).load(backRightFoot)
  const backRightPart1Model = useLoader(STLLoader).load(backRightPart1)
  const backRightPart2Model = useLoader(STLLoader).load(backRightPart2)
  const backRightVerrin1Model = useLoader(STLLoader).load(backRightVerrin1)
  const backRightVerrin2Model = useLoader(STLLoader).load(backRightVerrin2)
  const bodyModel = useLoader(STLLoader).load(body)
  const frontLeftExtensionModel = useLoader(STLLoader).load(frontLeftExtension)
  const frontLeftFootModel = useLoader(STLLoader).load(frontLeftFoot)
  const frontLeftPart1Model = useLoader(STLLoader).load(frontLeftPart1)
  const frontLeftPart2Model = useLoader(STLLoader).load(frontLeftPart2)
  const frontLeftVerrin1Model = useLoader(STLLoader).load(frontLeftVerrin1)
  const frontLeftVerrin2Model = useLoader(STLLoader).load(frontLeftVerrin2)
  const frontRightExtensionModel
    = useLoader(STLLoader).load(frontRightExtension)
  const frontRightFootModel = useLoader(STLLoader).load(frontRightFoot)
  const frontRightPart1Model = useLoader(STLLoader).load(frontRightPart1)
  const frontRightPart2Model = useLoader(STLLoader).load(frontRightPart2)
  const frontRightVerrin1Model = useLoader(STLLoader).load(frontRightVerrin1)
  const frontRightVerrin2Model = useLoader(STLLoader).load(frontRightVerrin2)
  const sectionAModel = useLoader(STLLoader).load(sectionA)
  const sectionBModel = useLoader(STLLoader).load(sectionB)
  const sectionCModel = useLoader(STLLoader).load(sectionC)
  const sectionDModel = useLoader(STLLoader).load(sectionD)
  const sectionEModel = useLoader(STLLoader).load(sectionE)
  const sectionFModel = useLoader(STLLoader).load(sectionF)
  const verrinModel = useLoader(STLLoader).load(verrin)
  const verticalAxisModel = useLoader(STLLoader).load(verticalAxis)

  const props: Props<typeof Group> = $props()
</script>

<T.Group
  name="MaxiPrinter"
  rotation.x={-Math.PI / 2}
  scale={0.001}
  {...props}
>
  {#if $bodyModel}
    <T.Mesh
      name="bodyModel"
      castShadow
    >
      <T is={$bodyModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontLeftExtensionModel}
    <T.Mesh
      name="frontLeftExtensionModel"
      castShadow
    >
      <T is={$frontLeftExtensionModel} />
      <T.MeshStandardMaterial
        color="black"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontLeftFootModel}
    <T.Mesh
      name="frontLeftFootModel"
      castShadow
    >
      <T is={$frontLeftFootModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontLeftPart1Model}
    <T.Mesh
      name="frontLeftPart1Model"
      castShadow
    >
      <T is={$frontLeftPart1Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontLeftPart2Model}
    <T.Mesh
      name="frontLeftPart2Model"
      castShadow
    >
      <T is={$frontLeftPart2Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontLeftVerrin1Model}
    <T.Mesh
      name="frontLeftVerrin1Model"
      castShadow
    >
      <T is={$frontLeftVerrin1Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontLeftVerrin2Model}
    <T.Mesh
      name="frontLeftVerrin2Model"
      castShadow
    >
      <T is={$frontLeftVerrin2Model} />
      <T.MeshStandardMaterial
        color="gray"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontRightExtensionModel}
    <T.Mesh
      name="frontRightExtensionModel"
      castShadow
    >
      <T is={$frontRightExtensionModel} />
      <T.MeshStandardMaterial
        color="black"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontRightFootModel}
    <T.Mesh
      name="frontRightFootModel"
      castShadow
    >
      <T is={$frontRightFootModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontRightPart1Model}
    <T.Mesh
      name="frontRightPart1Model"
      castShadow
    >
      <T is={$frontRightPart1Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontRightPart2Model}
    <T.Mesh
      name="frontRightPart2Model"
      castShadow
    >
      <T is={$frontRightPart2Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontRightVerrin1Model}
    <T.Mesh
      name="frontRightVerrin1Model"
      castShadow
    >
      <T is={$frontRightVerrin1Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $frontRightVerrin2Model}
    <T.Mesh
      name="frontRightVerrin2Model"
      castShadow
    >
      <T is={$frontRightVerrin2Model} />
      <T.MeshStandardMaterial
        color="gray"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backLeftExtensionModel}
    <T.Mesh
      name="backLeftExtensionModel"
      castShadow
    >
      <T is={$backLeftExtensionModel} />
      <T.MeshStandardMaterial
        color="black"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backLeftFootModel}
    <T.Mesh
      name="backLeftFootModel"
      castShadow
    >
      <T is={$backLeftFootModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backLeftPart1Model}
    <T.Mesh
      name="backLeftPart1Model"
      castShadow
    >
      <T is={$backLeftPart1Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backLeftPart2Model}
    <T.Mesh
      name="backLeftPart2Model"
      castShadow
    >
      <T is={$backLeftPart2Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backLeftVerrin1Model}
    <T.Mesh
      name="backLeftVerrin1Model"
      castShadow
    >
      <T is={$backLeftVerrin1Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backLeftVerrin2Model}
    <T.Mesh
      name="backLeftVerrin2Model"
      castShadow
    >
      <T is={$backLeftVerrin2Model} />
      <T.MeshStandardMaterial
        color="gray"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backRightExtensionModel}
    <T.Mesh
      name="backRightExtensionModel"
      castShadow
    >
      <T is={$backRightExtensionModel} />
      <T.MeshStandardMaterial
        color="black"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backRightFootModel}
    <T.Mesh
      name="backRightFootModel"
      castShadow
    >
      <T is={$backRightFootModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backRightPart1Model}
    <T.Mesh
      name="backRightPart1Model"
      castShadow
    >
      <T is={$backRightPart1Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backRightPart2Model}
    <T.Mesh
      name="backRightPart2Model"
      castShadow
    >
      <T is={$backRightPart2Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backRightVerrin1Model}
    <T.Mesh
      name="backRightVerrin1Model"
      castShadow
    >
      <T is={$backRightVerrin1Model} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $backRightVerrin2Model}
    <T.Mesh
      name="backRightVerrin2Model"
      castShadow
    >
      <T is={$backRightVerrin2Model} />
      <T.MeshStandardMaterial
        color="gray"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $sectionAModel}
    <T.Mesh
      name="sectionAModel"
      castShadow
    >
      <T is={$sectionAModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $sectionBModel}
    <T.Mesh
      name="sectionBModel"
      castShadow
    >
      <T is={$sectionBModel} />
      <T.MeshStandardMaterial
        color="black"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $sectionCModel}
    <T.Mesh
      name="sectionCModel"
      castShadow
    >
      <T is={$sectionCModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $sectionDModel}
    <T.Mesh
      name="sectionDModel"
      castShadow
    >
      <T is={$sectionDModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $sectionEModel}
    <T.Mesh
      name="sectionEModel"
      castShadow
    >
      <T is={$sectionEModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $sectionFModel}
    <T.Mesh
      name="sectionFModel"
      castShadow
    >
      <T is={$sectionFModel} />
      <T.MeshStandardMaterial
        color="black"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $verrinModel}
    <T.Mesh
      name="verrinModel"
      castShadow
    >
      <T is={$verrinModel} />
      <T.MeshStandardMaterial
        color="black"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  {#if $verticalAxisModel}
    <T.Mesh
      name="verticalAxisModel"
      castShadow
    >
      <T is={$verticalAxisModel} />
      <T.MeshStandardMaterial
        color="#006045"
        metalness={0.8}
        roughness={0.5}
      />
    </T.Mesh>
  {/if}
  <T.MeshStandardMaterial
    metalness={0.8}
    roughness={0.5}
  />
</T.Group>
